import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { FileInput, Select, Textarea } from '@/components/Form'
import { OfferDocumentTypes } from '@/features/offers/constants'
import useOfferGCPR from '@/features/offers/hooks/useOfferGCPR'
import useOfferBookingApproval from '@/features/offers/hooks/useOfferBookingApproval'
import useOfferPRApproval from '@/features/offers/hooks/useOfferPRApproval'
import useOfferQuotationApproval from '@/features/offers/hooks/useOfferQuotationApproval'
import { APPROVAL_TABLE_TYPE } from '@/features/offers/offer-detail/helper/approverTableColumn.helper'
import useUser from '@/features/users/useUser'
import { authSelector } from '@/stores/auth/authSlice'
import { selectApprovalInvitedUsers, setApprovalInvitedUsers } from '@/stores/offers/offersSlice'
import { hideModal } from '@/stores/ui/modalSlice'
import { toast } from 'react-toastify'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

const AddInvitationModal = ({
  offerId,
  costBudgetIndex,
  vendorSelectionIndex,
  submissionIndex,
  approverIndex,
  tableType,
}) => {
  const invitedUsers = useSelector(selectApprovalInvitedUsers)
  const { user: currentUser } = useSelector(authSelector)

  const { useInvitePRUser } = useOfferPRApproval()
  const { useInviteBookingUser } = useOfferBookingApproval()
  const { useInviteQuotationUser } = useOfferQuotationApproval()
  const { useInviteGCPRUser } = useOfferGCPR()
  const invitePRUser = useInvitePRUser()
  const inviteBookingUser = useInviteBookingUser()
  const inviteQuotationUser = useInviteQuotationUser()
  const inviteGCPRUser = useInviteGCPRUser()

  const methods = useForm()
  const dispatch = useDispatch()
  const currentTableType = tableType ?? APPROVAL_TABLE_TYPE.PR_APPROVAL

  const getHeaderTitle = () => {
    switch (currentTableType) {
      case APPROVAL_TABLE_TYPE.BOOKING_APPROVAL:
        return 'Invite User to Booking Approval'
      case APPROVAL_TABLE_TYPE.QUOTATION_APPROVAL:
        return 'Invite User to Quotation Approval'
      case APPROVAL_TABLE_TYPE.GCPR_APPROVAL:
        return 'Invite User to GCPR Approval'
      case APPROVAL_TABLE_TYPE.PR_APPROVAL:
      default:
        return 'Invite User to PR Approval'
    }
  }

  const { useUserOptions, useFetchAllUsers } = useUser()
  const { data: allUsersResponse } = useFetchAllUsers({ page: 1, pageSize: 0 })
  const { data: allUserOptions } = useUserOptions({ valueKey: '_id', labelKey: 'fullName' })

  const allUsers = allUsersResponse?.data || []
  const userOptions = allUserOptions.filter((user) => user?._id !== currentUser?._id)

  const closeModal = () => {
    dispatch(hideModal())
  }

  const onSubmit = async ({ userId, remark, files }) => {
    if (!userId || userId === '') {
      toast.error('Please select a user!')
      return
    }
    if (!remark || remark === '') {
      toast.error('Please enter a remark!')
      return
    }

    const selectedUser = allUsers.find((user) => user?._id === userId)
    if (!selectedUser) {
      toast.error('[Unexpected Error] Missing selected user detail')
      return
    }

    const normalizedFiles = Array.isArray(files)
      ? files.map((file) => file?._id || file).filter((id) => id)
      : files?._id
        ? [files._id]
        : files
          ? [files]
          : []

    try {
      dispatch(showLoadingOverlay())
      switch (currentTableType) {
        case APPROVAL_TABLE_TYPE.PR_APPROVAL:
          await invitePRUser.mutateAsync({
            offerId,
            costBudgetIndex,
            vendorSelectionIndex,
            submissionIndex,
            approverIndex: approverIndex,
            userId,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.BOOKING_APPROVAL:
          await inviteBookingUser.mutateAsync({
            offerId,
            approverIndex,
            userId,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.QUOTATION_APPROVAL:
          await inviteQuotationUser.mutateAsync({
            offerId,
            approverIndex,
            userId,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.GCPR_APPROVAL:
          await inviteGCPRUser.mutateAsync({
            offerId,
            submissionIndex,
            approverIndex,
            userId,
            remark,
            files: normalizedFiles,
          })
          break
        default:
          throw new Error('Unsupported table type for invitation')
      }

      methods.reset({ userId: '', remark: '', files: [] })
      dispatch(
        setApprovalInvitedUsers([
          ...invitedUsers,
          { user: selectedUser, remark, status: 'Invited', files: normalizedFiles },
        ])
      )
      closeModal()
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={true} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>{getHeaderTitle()}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Flex direction="column" gap="4">
                <Select name="userId" label="User" options={userOptions} isRow />
                <Textarea name="remark" label="Reason for Invitation" row="5" />
                <FileInput
                  label={'Attachment'}
                  name="files"
                  fileType={OfferDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
                  isRow
                  canUpload
                  isMultiple
                  isRequired={false}
                />
              </Flex>
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button
                  colorScheme="green"
                  type="submit"
                  isLoading={
                    invitePRUser.isLoading ||
                    inviteBookingUser.isLoading ||
                    inviteQuotationUser.isLoading ||
                    inviteGCPRUser.isLoading
                  }
                >
                  Invite
                </Button>
                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default AddInvitationModal
